import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  inquiriesApi,
  INQUIRY_CATEGORY_LABEL,
  INQUIRY_STATUS_LABEL,
  INQUIRY_STATUS_TONE,
  type Inquiry,
  type InquiryCategory,
  type InquiryStatus,
  type GetInquiriesParams,
} from '@/services/api';
import {
  PageHeader,
  FilterBar,
  FilterField,
  Select,
  Pagination,
} from '@/components/common';
import {
  DataGrid,
  DateCell,
  makeBadgeCell,
  makeActionCell,
  RowNumberCell,
} from '@/components/grid';

type CategoryFilter = InquiryCategory | 'ALL';
type StatusFilter = InquiryStatus | 'ALL';

const PAGE_SIZE_OPTIONS = [10, 30, 50, 100];

export function InquiryListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pageIndex, setPageIndex] = useState(0);
  const [size, setSize] = useState(10);
  const [appliedCategory, setAppliedCategory] = useState<CategoryFilter>('ALL');
  const [appliedStatus, setAppliedStatus] = useState<StatusFilter>('ALL');

  const [localCategory, setLocalCategory] = useState<CategoryFilter>('ALL');
  const [localStatus, setLocalStatus] = useState<StatusFilter>('ALL');
  const [localSize, setLocalSize] = useState(10);

  const queryParams = useMemo<GetInquiriesParams>(
    () => ({
      page: pageIndex,
      size,
      category: appliedCategory,
      status: appliedStatus,
    }),
    [pageIndex, size, appliedCategory, appliedStatus],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['inquiries', queryParams],
    queryFn: () => inquiriesApi.getInquiries(queryParams),
    placeholderData: prev => prev,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const rows = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;
  const startIndex = pageIndex * size;

  const removeInquiry = useMutation({
    mutationFn: (row: Inquiry) => inquiriesApi.deleteInquiry(row.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
    onError: err => {
      alert(err instanceof Error ? err.message : '삭제에 실패했습니다.');
    },
  });

  const handleSearch = () => {
    setPageIndex(0);
    setSize(localSize);
    setAppliedCategory(localCategory);
    setAppliedStatus(localStatus);
  };

  const handleReset = () => {
    setLocalCategory('ALL');
    setLocalStatus('ALL');
    setLocalSize(10);
    setPageIndex(0);
    setSize(10);
    setAppliedCategory('ALL');
    setAppliedStatus('ALL');
  };

  const columns = useMemo<ColDef<Inquiry>[]>(
    () => [
      {
        headerName: '번호',
        width: 72,
        sortable: false,
        cellRenderer: RowNumberCell,
        cellRendererParams: { startIndex },
      },
      {
        headerName: '접수일',
        field: 'createdAt',
        width: 150,
        cellRenderer: DateCell,
      },
      {
        headerName: '구분',
        field: 'category',
        width: 120,
        cellRenderer: makeBadgeCell({
          toneMap: {
            STORE_INFO: 'info',
            BIZ_IMPROVE: 'primary',
            ETC: 'neutral',
          },
          labelMap: INQUIRY_CATEGORY_LABEL,
        }),
      },
      {
        headerName: '매장 · 내용',
        flex: 1.6,
        minWidth: 300,
        sortable: false,
        cellRenderer: (p: ICellRendererParams<Inquiry>) => {
          const row = p.data;
          if (!row) return '-';
          return (
            <div className="flex h-full flex-col justify-center py-1 leading-tight">
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-gray-800">
                  {row.companyName || '(매장 미지정)'}
                </span>
                {row.subType ? (
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500">
                    {row.subType}
                  </span>
                ) : null}
              </div>
              <span className="truncate text-xs text-gray-500" title={row.content}>
                {row.content}
              </span>
            </div>
          );
        },
      },
      {
        headerName: '상태',
        field: 'status',
        width: 100,
        cellRenderer: makeBadgeCell({
          toneMap: INQUIRY_STATUS_TONE,
          labelMap: INQUIRY_STATUS_LABEL,
        }),
      },
      {
        headerName: '관리',
        width: 150,
        sortable: false,
        cellRenderer: makeActionCell<Inquiry>([
          {
            label: '상세',
            onClick: row => navigate(`/inquiries/${row.id}`),
          },
          {
            label: '삭제',
            variant: 'danger',
            onClick: row => {
              if (
                window.confirm(
                  `문의를 삭제하시겠습니까?\n${row.companyName || `(문의 #${row.id})`}`,
                )
              ) {
                removeInquiry.mutate(row);
              }
            },
          },
        ]),
      },
    ],
    [navigate, startIndex, removeInquiry],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="문의사항"
        subtitle="보호자가 남긴 매장 정보 수정·제안(및 사업주 개선사항)을 접수·처리합니다."
      />

      <FilterBar onSubmit={handleSearch} onReset={handleReset}>
        <FilterField flex="flex-none" className="md:w-44">
          <Select
            value={localCategory}
            onChange={e => setLocalCategory(e.target.value as CategoryFilter)}
          >
            <option value="ALL">전체 구분</option>
            <option value="STORE_INFO">매장정보 제안</option>
            <option value="BIZ_IMPROVE">개선사항</option>
            <option value="ETC">기타</option>
          </Select>
        </FilterField>
        <FilterField flex="flex-none" className="md:w-40">
          <Select
            value={localStatus}
            onChange={e => setLocalStatus(e.target.value as StatusFilter)}
          >
            <option value="ALL">전체 상태</option>
            <option value="RECEIVED">접수</option>
            <option value="IN_PROGRESS">처리중</option>
            <option value="DONE">완료</option>
            <option value="REJECTED">반려</option>
          </Select>
        </FilterField>
        <FilterField flex="flex-none" className="md:w-28">
          <Select
            value={localSize}
            onChange={e => setLocalSize(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>
                {n}개
              </option>
            ))}
          </Select>
        </FilterField>
      </FilterBar>

      <DataGrid<Inquiry>
        columns={columns}
        data={rows}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        error={error}
        onRetry={() => refetch()}
        getRowId={row => String(row.id)}
        emptyMessage="접수된 문의가 없습니다."
      />

      <Pagination
        pageIndex={pageIndex}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={size}
        onPageChange={setPageIndex}
      />
    </div>
  );
}
